import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from "@/components/ui/button";
import { AlertCircle, Info, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { isUserWorkSpaceOwner } from "@common-reducers/UserSelectors";
import { creditNudgeTier, dismissNudge, isCreditNudgesEnabled, isNudgeDismissed } from "./credit-nudge";

export interface CreditWarningAlertProps {
  creditsRemaining: number;
  percentageUsed: number;
  isLoading?: boolean;
  /** Omit for non-owners: the alert is shown without the buy button. */
  onAddCredits?: () => void;
  className?: string;
}

/**
 * Reusable credit warning alert component
 * Shows different warning levels based on credit usage:
 * - Red alert: Out of credits (creditsRemaining <= 0)
 * - Amber alert: Low credits (percentageUsed > 80% AND creditsRemaining > 0)
 */
export const CreditWarningAlert: React.FC<CreditWarningAlertProps> = ({
  creditsRemaining,
  percentageUsed,
  isLoading = false,
  onAddCredits,
  className = ""
}) => {
  const { t } = useTranslation(namespace);
  const isOwner = useAppSelector(isUserWorkSpaceOwner);
  const [dismissed, setDismissed] = React.useState<boolean>(() => isNudgeDismissed());

  // Don't show anything while loading
  if (isLoading) {
    return null;
  }

  // #357 change 3 (flag, default OFF): 50/80/100% nudges, owner-only CTA, member "ask the owner".
  if (isCreditNudgesEnabled()) {
    const tier = creditNudgeTier(percentageUsed, creditsRemaining);
    if (!tier || (tier === '50' && dismissed)) return null;
    const palette = tier === '100'
      ? { bg: 'var(--credits-alert-danger-bg)', border: 'var(--credits-alert-danger-border)', text: 'var(--credits-alert-danger-text)', title: 'var(--credits-alert-danger-title)' }
      : tier === '80'
        ? { bg: 'var(--credits-alert-warning-bg)', border: 'var(--credits-alert-warning-border)', text: 'var(--credits-alert-warning-text)', title: 'var(--credits-alert-warning-title)' }
        : { bg: 'var(--credits-alert-info-bg)', border: 'var(--credits-alert-info-border)', text: 'var(--credits-alert-info-text)', title: 'var(--credits-alert-info-text)' };
    const Icon = tier === '50' ? Info : AlertCircle;
    const pct = Math.round(percentageUsed);
    return (
      <Alert className={`flex items-start gap-3 ${className}`} data-testid={`credit-nudge-${tier}`}
        style={{ backgroundColor: palette.bg, borderColor: palette.border }}>
        <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: palette.text }} />
        <div className="flex-1 min-w-0">
          <AlertTitle className="text-left" style={{ color: palette.title }}>
            {t(`credits.nudge${tier}Title`, tier === '100' ? 'Out of credits' : tier === '80' ? 'Credits running low' : 'Half of your credits used')}
          </AlertTitle>
          <AlertDescription className="text-left" style={{ color: palette.text }}>
            {t(`credits.nudge${tier}Message`, {
              pct,
              defaultValue: tier === '100'
                ? 'You are out of credits. Your agents may stop replying until you add more.'
                : tier === '80'
                  ? 'You have used {{pct}}% of your credits. Add credits now so your agents do not stop mid-conversation.'
                  : 'You have used {{pct}}% of your credits. You can top up any time to keep your agents running.',
            })}
            <div className="mt-2">
              {isOwner && onAddCredits ? (
                <Button size="sm" className="h-auto" onClick={onAddCredits}
                  style={{ backgroundColor: 'var(--credits-button-primary)', color: 'var(--credits-button-primary-text)', borderColor: 'var(--credits-button-primary)' }}>
                  {t('credits.addCreditsAction', 'Add Credits')}
                </Button>
              ) : !isOwner ? (
                <span className="text-sm font-medium">{t('credits.nudgeAskOwner', 'Ask your workspace owner to add credits.')}</span>
              ) : null}
            </div>
          </AlertDescription>
        </div>
        {tier === '50' && (
          <button type="button" aria-label={t('credits.nudgeDismiss', 'Dismiss')} className="flex-shrink-0 opacity-70 hover:opacity-100"
            style={{ color: palette.text }} onClick={() => { dismissNudge(); setDismissed(true); }}>
            <X className="h-4 w-4" />
          </button>
        )}
      </Alert>
    );
  }

  // Out of credits warning (red alert)
  if (creditsRemaining <= 0) {
    return (
      <Alert 
        className={`flex items-start gap-3 ${className}`}
        style={{
          backgroundColor: 'var(--credits-alert-danger-bg)',
          borderColor: 'var(--credits-alert-danger-border)'
        }}
      >
        <AlertCircle 
          className="h-4 w-4 flex-shrink-0 mt-0.5" 
          style={{ color: 'var(--credits-alert-danger-text)' }}
        />
        <div className="flex-1 min-w-0">
          <AlertTitle 
            className="text-left"
            style={{ color: 'var(--credits-alert-danger-title)' }}
          >
            {t('credits.outOfCreditsTitle', 'Out of Credits')}
          </AlertTitle>
          <AlertDescription 
            className="text-left"
            style={{ color: 'var(--credits-alert-danger-text)' }}
          >
            {t('credits.outOfCreditsMessage', 'You have run out of credits. Your agents may stop working. Please add more credits or upgrade your plan to restore service.')}
            <div className="mt-2">
              {onAddCredits && <Button 
                size="sm" 
                className="h-auto" 
                onClick={onAddCredits}
                style={{
                  backgroundColor: '#4dbdeb',
                  color: 'var(--color-always-white)',
                  borderColor: 'var(--credits-button-primary)'
                }}
              >
                {t('credits.addCreditsAction', 'Add Credits')}
              </Button>}
            </div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  // Low credits warning (amber alert)
  if (percentageUsed > 80 && creditsRemaining > 0) {
    return (
      <Alert 
        className={`flex items-start gap-3 ${className}`}
        style={{
          backgroundColor: 'var(--credits-alert-warning-bg)',
          borderColor: 'var(--credits-alert-warning-border)'
        }}
      >
        <AlertCircle 
          className="h-4 w-4 flex-shrink-0 mt-0.5" 
          style={{ color: 'var(--credits-alert-warning-text)' }}
        />
        <div className="flex-1 min-w-0">
          <AlertTitle 
            className="text-left"
            style={{ color: 'var(--credits-alert-warning-title)' }}
          >
            {t('credits.lowCreditsWarning', 'Low Credits Warning')}
          </AlertTitle>
          <AlertDescription 
            className="text-left"
            style={{ color: 'var(--credits-alert-warning-text)' }}
          >
            {t('credits.lowCreditsMessage', 'You are running low on credits. Consider upgrading your plan or purchasing additional credits to ensure uninterrupted service.')}
            <div className="mt-2">
              {onAddCredits && <Button 
                size="sm" 
                className="h-auto" 
                onClick={onAddCredits}
                style={{
                  backgroundColor: 'var(--credits-button-primary)',
                  color: 'var(--credits-button-primary-text)',
                  borderColor: 'var(--credits-button-primary)'
                }}
              >
                {t('credits.addCreditsAction', 'Add Credits')}
              </Button>}
            </div>
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  // No warning needed
  return null;
};

export default CreditWarningAlert; 